import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'react-responsive-modal';
import 'react-responsive-modal/styles.css';
import CreneauPicker from 'components/CreneauPicker';
import Policy from 'components/Policy';
import { ReactComponent as CalendarIcon } from 'assets/icons/calendar.svg';

const UserFormDialog = (props) => {
  const {
    open,
    onClose,
    onChange,
    onSubmit,
    requestDate,
    initialValues = {},
  } = props;

  const fields = [
    { name: 'lastname', placeholder: 'Nom', type: 'text' },
    { name: 'firstname', placeholder: 'Prénom', type: 'text' },
    { name: 'mobilephone', placeholder: 'Téléphone', type: 'tel' },
    { name: 'email', placeholder: 'E-mail', type: 'email' },
  ];

  const [showPicker, setShowPicker] = useState(false);
  const [policy, setPolicy] = useState(false);

  const [formData, setFormData] = useState({
    ...initialValues,
    lastname: '',
    firstname: '',
    mobilephone: '',
    email: '',
    date: null,
    creneau: 'Matin',
  });

  const [touched, setTouched] = useState({});
  const [errors, setErrors] = useState({});

  const validate = (formData) => {
    const errors = {};

    fields.forEach((field) => {
      if (!formData[field.name]) {
        errors[field.name] = `Le champ ${field.placeholder} est requis`;
      }
    });

    if (requestDate && !formData.date) {
      errors.date = 'Ce champs est requis';
    }

    return errors;
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
    const newErrors = validate(formData);
    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValues = { ...formData, [name]: value };
    const errors = validate(newValues);

    setErrors(errors);

    if (Object.keys(errors).length === 0) {
      onChange(newValues);
    }

    setFormData(newValues);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allTouched = {};
    fields.forEach((field) => {
      allTouched[field.name] = true;
    });
    if (requestDate) {
      allTouched.date = true;
    }
    setTouched(allTouched);

    const newErrors = validate(formData);
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      onSubmit(formData);
    }
  };

  const labelClassName = 'block mb-2 text-[#3A3A3A] text-sm';
  const inputClassName =
    'py-3 px-3 box-border bg-[#F5F5F5] border-gray-300 rounded';
  const errorClassName = 'text-red-500 text-xs';

  const submitable =
    Object.keys(errors).length === 0 &&
    Object.keys(touched).length > 0 &&
    policy;

  return (
    <Modal
      open={open}
      onClose={onClose}
      center
      classNames={{
        root: 'user_form_dialog md:pt-0 z-[10000000]',
        modal: 'p-0 border-box',
      }}
    >
      <div className="p-4 calculette max-w-[400px]">
        <h2 className="text-lg text-center text-main mb-4 md:text-2xl !mt-5">
          {requestDate
            ? 'Vous souhaitez être rappelé ?'
            : 'Concrétisez votre projet de rénovation'}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          {fields.map((field) => (
            <div key={field.name}>
              <label
                htmlFor={`user-form-${field.name}`}
                className={labelClassName}
              >
                {field.placeholder}*
              </label>
              <input
                id={`user-form-${field.name}`}
                type={field.type}
                name={field.name}
                placeholder={field.placeholder}
                value={formData[field.name]}
                onChange={handleChange}
                onBlur={() => handleBlur(field.name)}
                className={`${inputClassName} w-full ${
                  touched[field.name] && errors[field.name]
                    ? 'border border-red-400 border-solid'
                    : 'border-none'
                } rounded`}
                required
              />
              {touched[field.name] && errors[field.name] && (
                <p style={{ marginTop: 4 }} className={errorClassName}>
                  {errors[field.name]}
                </p>
              )}
            </div>
          ))}

          {requestDate && (
            <div
              onClick={() => setShowPicker(true)}
              className="flex flex-col cursor-pointer"
            >
              <label className={labelClassName}>Définir une date</label>
              <div
                className={`w-fit px-3 py-2 flex flex-row items-center space-x-2 mb-1 box-border bg-[#F5F5F5] border-gray-300 rounded ${
                  touched.date && errors.date
                    ? 'border border-red-400 border-solid'
                    : 'border-none'
                }`}
              >
                <span className={`h-full w-24`}>
                  {!formData.date && (
                    <p className="text-gray-300 !text-sm pointer-events-none">
                      27-01-2025
                    </p>
                  )}
                  {formData.date?.toISOString()?.split('T')?.[0]}
                </span>
                <CalendarIcon />
              </div>

              <input
                type="date"
                name="date"
                value={formData.date?.toISOString()?.split('T')?.[0]}
                onClick={(e) => {
                  e.preventDefault();
                }}
                onBlur={() => handleBlur('date')}
                className={'hidden'}
              />
              {touched.date && errors.date && (
                <p className={errorClassName}>{errors.date}</p>
              )}
            </div>
          )}
          <div className={''}>
            <Policy
              onChange={(e) => {
                setPolicy(e.target.checked);
              }}
            />
          </div>
          {showPicker && (
            <CreneauPicker
              callBtn={() => setShowPicker(false)}
              creneau={formData.creneau}
              date={formData.date}
              onClose={(date) => {
                if (!date) {
                  setTouched((prev) => ({ ...prev, date: true }));
                }
                handleChange({ target: { name: 'date', value: date } });
              }}
              handleDate={(date) => {
                if (date) {
                  setTouched((prev) => ({ ...prev, date: true }));
                  handleChange({ target: { name: 'date', value: date } });
                }
                const newValues = { ...formData, date };
                setFormData(newValues);
                onChange(newValues);
              }}
              handleCreneau={(e) => {
                const newValues = { ...formData, creneau: e.target.value };
                setFormData(newValues);
                onChange(newValues);
              }}
              hidePicker={() => setShowPicker(false)}
              callBtnText={'Valider'}
            />
          )}
          <div className={'pt-4 flex justify-center'}>
            <button
              disabled={!submitable}
              type="submit"
              className="btn-nav-m"
              style={{
                width: 'auto',
                backgroundColor: submitable ? '#FBA03D' : '#959595',
              }}
            >
              {requestDate ? 'Être rappelé' : 'Prendre rendez-vous'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

UserFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  requestDate: PropTypes.bool,
  initialValues: PropTypes.object,
};

export default UserFormDialog;
